import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, storePath } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useStore } from "../../lib/store";
import type { Collection, Page as PageT, ProductSummary } from "../../lib/catalog";
import { Sparkles } from "lucide-react";
import { AIDrawer } from "../../components/AIPanel";
import { useAIEnabled } from "../../lib/ai";
import { Badge, BulkBar, Button, Card, confirmDialog, cx, EmptyState, ErrorState, Input, Modal, Page, SegmentedViews, Skeleton, useToast } from "../../components/ui";
import { TagsInput } from "../../components/TagsInput";
import { FilterPill, MenuButton } from "../../components/FilterPill";
import { ImportDialog, ExportDialog } from "./ImportExport";
import type { Key } from "../../locales/en";
import { SampleBadge, useSampleIds } from "../../components/SampleData";

const tabs = ["", "active", "draft", "archived"] as const;
// the product index sorts.
const sorts = ["title", "-title", "-created_at", "created_at", "-updated_at", "updated_at", "inventory", "-inventory", "product_type", "-product_type", "vendor", "-vendor"] as const;

export const statusTone = (s: string) => (s === "active" ? "success" : s === "draft" ? "info" : s === "archived" ? "neutral" : "warning") as "success";

export type ListFilter = { status: string; q: string; vendor: string; product_type: string; tag: string; collection_id: string; sort: string };

export function useDebounced<T>(v: T, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setD(v), ms);
    return () => clearTimeout(id);
  }, [v, ms]);
  return d;
}

function query(f: ListFilter, after = "") {
  const q = new URLSearchParams({ limit: "50", sort: f.sort });
  for (const k of ["status", "q", "vendor", "product_type", "tag", "collection_id"] as const) if (f[k]) q.set(k, f[k]);
  if (after) q.set("after", after);
  return q.toString();
}

export function ProductList() {
  const { t, num, locale } = useI18n();
  const { id, handle, can } = useStore();
  const sampleIds = useSampleIds("product");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<ListFilter>({ status: "", q: "", vendor: "", product_type: "", tag: "", collection_id: "", sort: "-updated_at" });
  const [search, setSearch] = useState("");
  const q = useDebounced(search);
  useEffect(() => setF((x) => ({ ...x, q })), [q]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [aiFor, setAiFor] = useState<string[] | null>(null); // the AI panel, with the picked products
  const aiEnabled = useAIEnabled(id);
  const [dialog, setDialog] = useState<"" | "import" | "export" | "add_tags" | "remove_tags" | "add_to_collections" | "remove_from_collections">("");
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [f]);

  const facets = useQuery({
    queryKey: ["store", id, "products", "facets"], enabled: !!id,
    queryFn: () => api<{ vendors: string[]; product_types: string[]; tags: { tag: string }[] }>(storePath(id!, "/products/facets")),
  });
  const collections = useQuery({
    queryKey: ["store", id, "collections", "all-for-filter"], enabled: !!id,
    queryFn: () => api<PageT<Collection>>(storePath(id!, "/collections?limit=250")),
  });
  const list = useInfiniteQuery({
    queryKey: ["store", id, "products", f], enabled: !!id, initialPageParam: "",
    queryFn: ({ pageParam }) => api<PageT<ProductSummary>>(storePath(id!, `/products?${query(f, pageParam)}`)),
    getNextPageParam: (last) => (last.page_info.has_next_page ? last.page_info.end_cursor : undefined),
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const filtered = !!(f.status || f.q || f.vendor || f.product_type || f.tag || f.collection_id);

  const bulk = useMutation({
    mutationFn: (body: Record<string, unknown>) => {
      const sel = allMatching
        ? { filter: { status: f.status ? [f.status] : undefined, vendor: f.vendor || undefined, product_type: f.product_type || undefined, tag: f.tag || undefined, collection_id: f.collection_id || undefined, q: f.q || undefined } }
        : { ids: [...selected] };
      return api<{ updated?: number; job_id?: string }>(storePath(id!, "/products/bulk"), { body: { ...body, ...sel } });
    },
    onSuccess: (r) => {
      toast(r.job_id ? t("products.bulk.started") : t("products.bulk.done", { n: r.updated ?? 0 }));
      setSelected(new Set());
      setAllMatching(false);
      setDialog("");
      qc.invalidateQueries({ queryKey: ["store", id, "products"] });
      qc.invalidateQueries({ queryKey: ["store", id, "collections"] });
    },
    onError: (e) => toast(e.message, true),
  });
  const count = allMatching ? Infinity : selected.size;
  const toggleAll = () => setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));
  // the inventory column.
  const inventory = (p: ProductSummary) => {
    if (p.tracked_variants === 0) return <span className="text-stone-500">{t("products.inv.untracked")}</span>;
    const text = p.variants_count > 1 ? t("products.inv.for_variants", { n: num(p.total_inventory), v: p.tracked_variants }) : t("products.inv.in_stock", { n: num(p.total_inventory) });
    return <span className={cx(p.total_inventory <= 0 && "text-red-700")}>{text}</span>;
  };
  const category = (p: ProductSummary) => (p.category ? p.category.names?.[locale] || p.category.name : "");
  const manualCollections = (collections.data?.data ?? []).filter((c) => c.kind === "manual");
  const sel = useMemo(() => selected, [selected]);

  const actions = (
    <>
      {can("products.edit") && aiEnabled && <Button onClick={() => setAiFor([])}><Sparkles className="size-4" />{t("ai.open")}</Button>}
      {can("products.edit") && <Button onClick={() => setDialog("import")}>{t("products.import")}</Button>}
      <Button onClick={() => setDialog("export")}>{t("products.export")}</Button>
      {can("products.edit") && <Link to="/store/$handle/products/new" params={{ handle }}><Button variant="primary">{t("products.add")}</Button></Link>}
    </>
  );

  if (list.isSuccess && rows.length === 0 && !filtered) {
    return (
      <Page title={t("products.title")} actions={actions}>
        <Card><EmptyState title={t("products.empty_title")} body={t("products.empty_body")}
          action={can("products.edit") && <div className="flex gap-2"><Link to="/store/$handle/products/new" params={{ handle }}><Button variant="primary">{t("products.add")}</Button></Link><Button onClick={() => setDialog("import")}>{t("products.import")}</Button></div>} /></Card>
        {dialog === "import" && <ImportDialog onClose={() => setDialog("")} />}
      </Page>
    );
  }

  return (
    <Page title={t("products.title")} actions={actions}>
      <Card className="overflow-hidden">
        <div className="-m-5">
          <div className="space-y-3 border-b border-line p-4">
            <SegmentedViews label={t("products.title")} value={f.status} onChange={(status) => setF({ ...f, status })}
              items={tabs.map((s) => ({ value: s, label: t(`products.tab.${s || "all"}` as Key) }))} />
            <div className="flex flex-wrap items-center gap-2">
              <Input type="search" className="min-w-48 flex-1" placeholder={t("products.search")} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t("products.search")} />
              <FilterPill label={t("products.filter.vendor")} value={f.vendor} onChange={(v) => setF({ ...f, vendor: v })}
                options={(facets.data?.vendors ?? []).map((v) => ({ value: v }))} searchPlaceholder={t("common.search")} clearLabel={t("products.filter.clear")} />
              <FilterPill label={t("products.filter.type")} value={f.product_type} onChange={(v) => setF({ ...f, product_type: v })}
                options={(facets.data?.product_types ?? []).map((v) => ({ value: v }))} searchPlaceholder={t("common.search")} clearLabel={t("products.filter.clear")} />
              <FilterPill label={t("products.filter.tag")} value={f.tag} onChange={(v) => setF({ ...f, tag: v })}
                options={(facets.data?.tags ?? []).map((v) => ({ value: v.tag }))} searchPlaceholder={t("common.search")} clearLabel={t("products.filter.clear")} />
              <FilterPill label={t("products.filter.collection")} value={f.collection_id} onChange={(v) => setF({ ...f, collection_id: v })}
                options={(collections.data?.data ?? []).map((c) => ({ value: c.id, label: c.title }))} searchPlaceholder={t("common.search")} clearLabel={t("products.filter.clear")} />
              {(f.vendor || f.product_type || f.tag || f.collection_id || search) && (
                <button type="button" className="text-sm text-brand hover:underline" onClick={() => { setSearch(""); setF({ ...f, vendor: "", product_type: "", tag: "", collection_id: "", q: "" }); }}>{t("products.filter.clear_all")}</button>
              )}
              <MenuButton label={<>⇅ <span className="hidden sm:inline">{t(`products.sort.${f.sort}` as Key)}</span></>}>
                {(close) => sorts.map((s) => (
                  <label key={s} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-stone-50">
                    <input type="radio" name="sort" checked={f.sort === s} onChange={() => { setF({ ...f, sort: s }); close(); }} />
                    {t(`products.sort.${s}` as Key)}
                  </label>
                ))}
              </MenuButton>
            </div>
          </div>

          {(selected.size > 0 || allMatching) && (
            <BulkBar label={allMatching ? t("products.all_matching_selected") : t("products.selected", { n: selected.size })}
              onClear={() => { setSelected(new Set()); setAllMatching(false); }}>
              {!allMatching && selected.size === rows.length && list.hasNextPage && (
                <Button size="sm" variant="plain" className="text-white hover:bg-white/10 hover:text-white" onClick={() => setAllMatching(true)}>{t("products.select_all_matching")}</Button>
              )}
              {can("products.edit") && (
                <>
                  {!allMatching && aiEnabled && <Button onClick={() => setAiFor([...selected])}><Sparkles className="size-4" />{t("ai.write_with_ai")}</Button>}
                  <Button disabled={bulk.isPending} onClick={() => bulk.mutate({ action: "set_status", status: "active" })}>{t("products.bulk.active")}</Button>
                  <Button disabled={bulk.isPending} onClick={() => bulk.mutate({ action: "set_status", status: "draft" })}>{t("products.bulk.draft")}</Button>
                  <MenuButton placement="top" label={t("products.bulk.more")}>
                    {(close) => {
                      const item = (label: string, run: () => void, critical = false) => (
                        <button key={label} type="button" role="menuitem" className={cx("block w-full rounded-lg px-2 py-1.5 text-start text-sm hover:bg-stone-50", critical && "text-red-700")}
                          onClick={() => { close(); run(); }}>{label}</button>
                      );
                      return (
                        <>
                          {item(t("products.bulk.archived"), () => bulk.mutate({ action: "set_status", status: "archived" }))}
                          {can("products.delete") && item(t("products.bulk.delete"), () => {
                            void confirmDialog({ title: t("products.bulk.delete_confirm", { n: count === Infinity ? "…" : count }), confirmLabel: t("common.delete"), tone: "critical" })
                              .then((ok) => ok && bulk.mutate({ action: "delete" }));
                          }, true)}
                          <div className="my-1 border-t border-stone-100" />
                          {item(t("products.bulk.add_tags"), () => setDialog("add_tags"))}
                          {item(t("products.bulk.remove_tags"), () => setDialog("remove_tags"))}
                          {item(t("products.bulk.add_collections"), () => setDialog("add_to_collections"))}
                          {item(t("products.bulk.remove_collections"), () => setDialog("remove_from_collections"))}
                          <div className="my-1 border-t border-stone-100" />
                          {item(t("products.bulk.publish"), () => bulk.mutate({ action: "publish" }))}
                          {item(t("products.bulk.unpublish"), () => bulk.mutate({ action: "unpublish" }))}
                        </>
                      );
                    }}
                  </MenuButton>
                </>
              )}
            </BulkBar>
          )}

          {list.isPending ? <div className="p-4"><Skeleton lines={6} /></div> : list.isError ? <div className="p-4"><ErrorState error={list.error} retry={() => list.refetch()} /></div> : rows.length === 0 ? (
            <p className="p-6 text-center text-sm text-stone-500">{t("products.none_match")}</p>
          ) : (
            <div className="overflow-x-auto"><table className="data-table w-full text-sm">
              <thead className="hidden text-start md:table-header-group">
                <tr>
                  <th className="w-10 px-3 py-2"><input type="checkbox" aria-label="select all" checked={selected.size === rows.length || allMatching} onChange={toggleAll} /></th>
                  <th className="px-3 py-2 text-start font-medium">{t("products.col.product")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("products.col.status")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("products.col.inventory")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("products.col.category")}</th>
                  <th className="px-3 py-2 text-start font-medium">{t("products.col.vendor")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((p) => (
                  <tr key={p.id} className="cursor-pointer" aria-selected={allMatching || sel.has(p.id)} onClick={() => navigate({ to: "/store/$handle/products/$productId", params: { handle, productId: p.id } })}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={p.title} checked={allMatching || sel.has(p.id)} onChange={(e) => {
                        const n = new Set(selected);
                        if (e.target.checked) n.add(p.id); else n.delete(p.id);
                        setSelected(n);
                        setAllMatching(false);
                      }} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <div className="size-10 shrink-0 overflow-hidden rounded-md bg-stone-100 ring-1 ring-stone-200">
                          {p.image && <img src={p.image} alt={p.image_alt ?? ""} className="size-full object-cover" loading="lazy" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-2"><span className="truncate font-medium">{p.title}</span><SampleBadge show={sampleIds.has(p.id)} /></div>
                          <div className="flex flex-wrap items-center gap-1 text-xs md:hidden"><Badge tone={statusTone(p.status)}>{t(`status.${p.status}` as Key)}</Badge>{inventory(p)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-3 py-2 md:table-cell"><Badge tone={statusTone(p.status)}>{t(`status.${p.status}` as Key)}</Badge></td>
                    <td className="hidden px-3 py-2 md:table-cell">{inventory(p)}</td>
                    <td className="hidden px-3 py-2 text-stone-600 md:table-cell">{category(p)}</td>
                    <td className="hidden px-3 py-2 text-stone-600 md:table-cell">{p.vendor}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          {list.hasNextPage && <div className="border-t border-stone-200 p-3 text-center"><Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>{t("products.load_more")}</Button></div>}
        </div>
      </Card>
      {dialog === "import" && <ImportDialog onClose={() => setDialog("")} />}
      {dialog === "export" && <ExportDialog filter={filtered ? f : null} selected={[...selected]} onClose={() => setDialog("")} />}
      {(dialog === "add_tags" || dialog === "remove_tags") && (
        <BulkTagsModal remove={dialog === "remove_tags"} title={t(dialog === "remove_tags" ? "products.bulk.remove_tags_title" : "products.bulk.add_tags_title", { n: count === Infinity ? "…" : count })} suggestions={facets.data?.tags.map((x) => x.tag) ?? []} saving={bulk.isPending}
          onClose={() => setDialog("")} onSave={(tags) => bulk.mutate({ action: dialog, tags })} />
      )}
      {(dialog === "add_to_collections" || dialog === "remove_from_collections") && (
        <BulkCollectionsModal remove={dialog === "remove_from_collections"} count={count} collections={manualCollections} saving={bulk.isPending}
          onClose={() => setDialog("")} onSave={(ids) => bulk.mutate({ action: dialog, collection_ids: ids })} />
      )}
      {aiFor && id && <AIDrawer storeId={id} productIds={aiFor} onClose={() => setAiFor(null)} />}
    </Page>
  );
}

export function BulkTagsModal({ remove, title, suggestions, saving, onClose, onSave }: {
  remove: boolean; title: string; suggestions: string[]; saving: boolean; onClose: () => void; onSave: (tags: string[]) => void;
}) {
  const { t } = useI18n();
  const [tags, setTags] = useState<string[]>([]);
  return (
    <Modal open size="sm" title={title} onClose={onClose}
      footer={<><Button onClick={onClose}>{t("common.cancel")}</Button><Button variant={remove ? "critical" : "primary"} disabled={tags.length === 0} loading={saving} onClick={() => onSave(tags)}>{t(remove ? "products.bulk.remove_tags" : "products.bulk.add_tags")}</Button></>}>
      <p className="text-sm text-stone-600">{t(remove ? "products.bulk.remove_tags_hint" : "products.bulk.add_tags_hint")}</p>
      <TagsInput value={tags} onChange={setTags} suggestions={suggestions} />
    </Modal>
  );
}

function BulkCollectionsModal({ remove, count, collections, saving, onClose, onSave }: {
  remove: boolean; count: number; collections: Collection[]; saving: boolean; onClose: () => void; onSave: (ids: string[]) => void;
}) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const n = count === Infinity ? "…" : count;
  const shown = collections.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal open title={t(remove ? "products.bulk.remove_collections_title" : "products.bulk.add_collections_title", { n })} onClose={onClose}
      footer={<><Button onClick={onClose}>{t("common.cancel")}</Button><Button variant={remove ? "critical" : "primary"} disabled={picked.size === 0} loading={saving} onClick={() => onSave([...picked])}>{t(remove ? "products.bulk.remove_collections" : "products.bulk.add_collections")}</Button></>}>
      <Input type="search" autoFocus placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} />
      {collections.length === 0 ? <p className="text-sm text-stone-500">{t("products.bulk.no_manual_collections")}</p> : (
        <ul className="-mx-5 max-h-72 divide-y divide-stone-100 overflow-auto border-y border-stone-100">
          {shown.map((c) => (
            <li key={c.id}>
              <label className="flex cursor-pointer items-center gap-3 px-5 py-2.5 text-sm hover:bg-stone-50">
                <input type="checkbox" className="accent-brand" checked={picked.has(c.id)} onChange={(e) => {
                  const next = new Set(picked);
                  if (e.target.checked) next.add(c.id); else next.delete(c.id);
                  setPicked(next);
                }} />
                <span className="flex-1 truncate">{c.title}</span>
                <span className="text-xs text-stone-500">{c.products_count}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
